// M6.1: the relayer's Ed25519 signature over a v1 message (mirrors the Rust
// client's `v1::check_and_sign`, sdk/rust/src/lib.rs), and a minimal
// JSON-RPC client over `fetch` that records every call verbatim for devnet
// evidence. Browser-safe (no `node:*` imports); Node-only I/O (keypair
// files, writing evidence to disk) lives in relayer-node.ts.

import { createKeyPairFromPrivateKeyBytes, getBase58Decoder, getBase58Encoder, getBase64Decoder, getBase64Encoder, signBytes } from '@solana/kit';
import { hex } from './digest.js';
import { type V1Message, checkV1, serializeV1 } from './v1.js';

export interface RelayerSigner {
  readonly publicKey: Uint8Array;
  sign(bytes: Uint8Array): Promise<Uint8Array>;
}

export type RelayerErrorCode = 'BadSeedLength' | 'NotFeePayer' | 'SimulationFailed' | 'RpcError' | 'ConfirmTimeout' | 'ConfirmFailed' | 'ConfirmAborted';

export class RelayerError extends Error {
  constructor(
    readonly code: RelayerErrorCode,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'RelayerError';
  }
}

/**
 * A signer from a 32-byte Ed25519 seed, through kit's WebCrypto key import. The private key is imported
 * non-extractable and the public key is the one `createKeyPairFromPrivateKeyBytes` returns beside it, so the signer
 * holds no CryptoKey whose seed can be exported (docs/18-one-fee-reserve.md section 9, asked for in docs/14). After
 * this release the app calls this only in the legacy escape hatch of docs/18 2.4; otherwise Node scripts and tests
 * use it, and the app's fee key signs inside the worker.
 */
export async function relayerFromSeed(seed32: Uint8Array): Promise<RelayerSigner> {
  if (seed32.length !== 32) throw new RelayerError('BadSeedLength', `relayer seed must be 32 bytes, got ${seed32.length}`);
  const { privateKey, publicKey: publicKeyHandle } = await createKeyPairFromPrivateKeyBytes(seed32, false);
  const publicKey = new Uint8Array(await crypto.subtle.exportKey('raw', publicKeyHandle));
  return { publicKey, sign: (bytes: Uint8Array) => signBytes(privateKey, bytes) };
}

/**
 * Sign a v1 message the way the Rust client's `check_and_sign` does: the
 * relayer must be the sole required signer (`numRequiredSignatures === 1`)
 * and `accountKeys[0]` (the fee payer) must be this relayer, `checkV1`'s
 * pre-flight rules must pass, and only then is the message signed. Returns
 * the full `VersionedTransaction` wire: `serializeV1(message)` (already
 * prefixed `0x81`) || the 64-byte signature, with no compact-u16 signature
 * count.
 *
 * This is the opposite order from a legacy/v0 transaction (compact-u16 count
 * || signatures || message) — SIMD-0385 moves the v1 message first and the
 * signature(s) to the tail, unprefixed, since the count is already the
 * message's own `numRequiredSignatures`. A 2026-09-18 devnet bug shipped the
 * legacy-style order for v1 too: agave's `simulateTransaction`/
 * `sendTransaction` decode the raw wire with `wincode` (matching
 * `solana-transaction`'s own `SchemaRead`, not the crate's separate,
 * bincode-compatible serde impl), which reads a v1 message by its `0x81`
 * first byte and then reads exactly `numRequiredSignatures` raw signatures
 * after it — so a leading count byte desyncs the whole parse and is
 * rejected with `"invalid message version"` (code -32602; see
 * `sdk/rust/tests/signed_v1_vectors.rs` and `docs/evidence/devnet/`).
 */
export async function signV1(message: V1Message, signer: RelayerSigner): Promise<Uint8Array> {
  if (message.numRequiredSignatures !== 1) throw new RelayerError('NotFeePayer', `v1 message requires ${message.numRequiredSignatures} signatures; this client signs alone as the sole required signer`);
  const feePayer = message.accountKeys[0];
  if (!feePayer || hex(feePayer) !== hex(signer.publicKey)) throw new RelayerError('NotFeePayer', 'the fee payer (accountKeys[0]) is not this relayer');
  checkV1(message);
  const messageBytes = serializeV1(message);
  const signature = await signer.sign(messageBytes);
  const wire = new Uint8Array(messageBytes.length + signature.length);
  wire.set(messageBytes, 0);
  wire.set(signature, messageBytes.length);
  return wire;
}

/**
 * A chain error, as the RPC reports it in a simulation's or a signature status's `err` (docs/18-one-fee-reserve.md
 * section 5.1 B; R78). `instruction`: an `InstructionError` raised by the instruction at `index`, with its inner error
 * (`{ Custom: n }` or a built-in name). `transaction`: an error of the transaction itself, raised by the runtime around
 * the instructions, such as `InsufficientFundsForFee` or `InsufficientFundsForRent { account_index }`; `accountIndex`
 * is kept whenever the error names one. `raw` is the error exactly as received.
 */
export type TransactionError = { kind: 'instruction'; index: number; inner: unknown; raw: unknown } | { kind: 'transaction'; name: string; accountIndex?: number; raw: unknown };

/**
 * Classify a chain error (5.1 B). Takes the RPC's raw `err` value, or a `SimulationFailed` / `ConfirmFailed`
 * `RelayerError` carrying one; null when there is no chain error (null or undefined, another `RelayerError`, any other
 * `Error`). Agave serialises a unit variant as its name (`"InsufficientFundsForFee"`) and any other variant as a
 * one-key object (`{ "InsufficientFundsForRent": { "account_index": 0 } }`, `{ "InstructionError": [0, { "Custom": 14 }] }`).
 */
export function transactionError(e: unknown): TransactionError | null {
  let err: unknown = e;
  if (e instanceof RelayerError) {
    if (e.code === 'SimulationFailed') err = (e.detail as { err?: unknown } | undefined)?.err;
    else if (e.code === 'ConfirmFailed') err = e.detail;
    else return null;
  } else if (e instanceof Error) {
    return null;
  }
  if (err === null || err === undefined) return null;
  if (typeof err === 'string') return { kind: 'transaction', name: err, raw: err };
  if (typeof err === 'object' && !Array.isArray(err)) {
    const keys = Object.keys(err);
    if (keys.length === 1) {
      const name = keys[0]!;
      const value = (err as Record<string, unknown>)[name];
      if (name === 'InstructionError' && Array.isArray(value) && value.length === 2 && typeof value[0] === 'number') return { kind: 'instruction', index: value[0], inner: value[1], raw: err };
      const index = value !== null && typeof value === 'object' ? (value as { account_index?: unknown }).account_index : undefined;
      return typeof index === 'number' ? { kind: 'transaction', name, accountIndex: index, raw: err } : { kind: 'transaction', name, raw: err };
    }
  }
  return { kind: 'transaction', name: 'Unrecognised', raw: err };
}

/** Custom error codes below this come from the vault program (program/src/lib.rs `VaultError`); Anchor programs (Jupiter, the venues) start at 6,000. */
export const VAULT_ERROR_CEILING = 100;

/**
 * Whether a chain error is a route failure (5.1 B): only an instruction-level error, with the app's existing rule for
 * its code (no `Custom` code, or a code of `VAULT_ERROR_CEILING` or more). A transaction-level error, a payer error
 * above all, is never one: it causes no BOUNDED re-quote, no `resend` and no venue exclusion.
 */
export function isRouteFailureError(e: unknown): boolean {
  const t = transactionError(e);
  if (!t || t.kind !== 'instruction') return false;
  const custom = t.inner !== null && typeof t.inner === 'object' ? (t.inner as { Custom?: unknown }).Custom : undefined;
  return typeof custom !== 'number' || custom >= VAULT_ERROR_CEILING;
}

/** One `getSignatureStatuses` entry as the RPC returns it. */
export interface SignatureStatus {
  err?: unknown;
  confirmationStatus?: string;
  slot?: number;
}

/**
 * The activation evidence predicate (docs/18 5.1 D step 4; R79): a status entry is evidence of this device's own
 * successful Initialize only when it is non-null, its `confirmationStatus` is `'finalized'` and its `err` field is
 * present and exactly null. A null entry (the network has no record), a `confirmed` entry, any error and an absent
 * `err` field are not evidence.
 */
export function isFinalizedSuccess(status: SignatureStatus | null | undefined): boolean {
  return status !== null && status !== undefined && status.confirmationStatus === 'finalized' && Object.prototype.hasOwnProperty.call(status, 'err') && status.err === null;
}

export function toBase58(bytes: Uint8Array): string {
  return getBase58Decoder().decode(bytes);
}
export function fromBase58(s: string): Uint8Array {
  return new Uint8Array(getBase58Encoder().encode(s));
}
function toBase64(bytes: Uint8Array): string {
  return getBase64Decoder().decode(bytes);
}
function fromBase64(s: string): Uint8Array {
  return new Uint8Array(getBase64Encoder().encode(s));
}

export interface EvidenceEntry {
  at: string;
  method: string;
  params: unknown;
  response: unknown;
}

/** Every RPC call this client made, verbatim, in order; exported as JSON for docs/evidence. */
export class EvidenceLog {
  private readonly entries: EvidenceEntry[] = [];
  record(method: string, params: unknown, response: unknown): void {
    this.entries.push({ at: new Date().toISOString(), method, params, response });
  }
  toJSON(): EvidenceEntry[] {
    return this.entries;
  }
}

interface JsonRpcOk<T> {
  jsonrpc: '2.0';
  id: number;
  result: T;
}
interface JsonRpcErr {
  jsonrpc: '2.0';
  id: number;
  error: { code: number; message: string; data?: unknown };
}
type JsonRpcResponse<T> = JsonRpcOk<T> | JsonRpcErr;

export interface RpcConfig {
  url: string;
  /** Total attempts per call, including the first; retries on HTTP 429 and network errors. Default 6. */
  maxAttempts?: number;
  fetchImpl?: typeof fetch;
  sleepImpl?: (ms: number) => Promise<void>;
}

export interface LatestBlockhash {
  blockhash: Uint8Array;
  slot: bigint;
  lastValidBlockHeight: bigint;
}

export interface OnChainAccount {
  data: Uint8Array;
  owner: Uint8Array;
  lamports: bigint;
}

/** Accounts read in one call, with that call's context slot. */
export interface AccountsAt {
  slot: number;
  accounts: (OnChainAccount | null)[];
}

export type ProgramAccountsFilter = { dataSize: number } | { memcmp: { offset: number; bytes: string; encoding?: 'base58' | 'base64' } };

export interface ProgramAccountsAt {
  slot: number;
  accounts: { pubkey: Uint8Array; account: OnChainAccount }[];
}

export interface SimulationResult {
  err: unknown;
  logs: string[];
  unitsConsumed: number | undefined;
  /** `context.slot` of the simulateTransaction response: the slot the cluster simulated against, not a transaction landing slot (R27). */
  slot: number;
}

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Minimal JSON-RPC client over `fetch`, retrying HTTP 429 and network errors with backoff, and recording every attempt verbatim in `evidence`. */
export class Rpc {
  readonly evidence = new EvidenceLog();
  private readonly url: string;
  private readonly maxAttempts: number;
  private readonly fetchImpl: typeof fetch;
  private readonly sleepImpl: (ms: number) => Promise<void>;
  private nextId = 1;

  constructor(config: RpcConfig) {
    this.url = config.url;
    this.maxAttempts = config.maxAttempts ?? 6;
    this.fetchImpl = config.fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => globalThis.fetch(input, init));
    this.sleepImpl = config.sleepImpl ?? defaultSleep;
  }

  private backoffMs(attempt: number): number {
    return Math.min(250 * 2 ** (attempt - 1), 8_000);
  }

  /**
   * One JSON-RPC call, retried on HTTP 429 and network errors. `opts.beforeAttempt` runs synchronously before every
   * attempt, the first and each retry: a throw there propagates untouched and no request is made (docs/18 section 6,
   * no send after Lock: the sweep's send checks it is still open before each attempt, retries included).
   */
  async call<T>(method: string, params: unknown[] = [], opts: { beforeAttempt?: () => void } = {}): Promise<T> {
    const id = this.nextId++;
    const body = { jsonrpc: '2.0' as const, id, method, params };
    let lastError: unknown;
    for (let attempt = 1; attempt <= this.maxAttempts; attempt++) {
      opts.beforeAttempt?.();
      try {
        const res = await this.fetchImpl(this.url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
        if (res.status === 429) {
          this.evidence.record(method, params, { httpStatus: 429 });
          lastError = new RelayerError('RpcError', `${method}: HTTP 429 (rate limited)`);
          if (attempt < this.maxAttempts) await this.sleepImpl(this.backoffMs(attempt));
          continue;
        }
        const json = (await res.json()) as JsonRpcResponse<T>;
        this.evidence.record(method, params, json);
        if ('error' in json) throw new RelayerError('RpcError', `${method}: ${json.error.message} (code ${json.error.code})`, json.error);
        return json.result;
      } catch (e) {
        // A JSON-RPC application error is the cluster's authoritative answer: never retried, unlike a 429 (looped via `continue` above) or a network exception.
        if (e instanceof RelayerError && e.code === 'RpcError' && e.detail !== undefined) throw e;
        lastError = e;
        if (attempt === this.maxAttempts) break;
        await this.sleepImpl(this.backoffMs(attempt));
      }
    }
    throw lastError instanceof Error ? lastError : new RelayerError('RpcError', `${method} failed after ${this.maxAttempts} attempts`);
  }

  async getSlot(commitment = 'confirmed'): Promise<number> {
    return this.call<number>('getSlot', [{ commitment }]);
  }

  /**
   * The endpoint's genesis hash, the base58 string the RPC returns, for the deployment check of
   * docs/18-one-fee-reserve.md 2.5. It is only compared with the built-in entry's tag, never a derivation input.
   */
  async getGenesisHash(): Promise<string> {
    return this.call<string>('getGenesisHash');
  }

  async getLatestBlockhash(commitment = 'confirmed'): Promise<LatestBlockhash> {
    const res = await this.call<{ context: { slot: number }; value: { blockhash: string; lastValidBlockHeight: number } }>('getLatestBlockhash', [{ commitment }]);
    return { blockhash: fromBase58(res.value.blockhash), slot: BigInt(res.context.slot), lastValidBlockHeight: BigInt(res.value.lastValidBlockHeight) };
  }

  async getAccountInfo(address: string, commitment = 'confirmed'): Promise<OnChainAccount | null> {
    const res = await this.call<{ context: { slot: number }; value: { data: [string, string]; owner: string; lamports: number } | null }>('getAccountInfo', [address, { encoding: 'base64', commitment }]);
    if (!res.value) return null;
    return { data: fromBase64(res.value.data[0]), owner: fromBase58(res.value.owner), lamports: BigInt(res.value.lamports) };
  }

  /**
   * Up to 100 accounts in one call, so in one context slot (docs/19 6.3 item 1): `null` for an
   * absent account, in the order asked. `minContextSlot` makes a lagging node refuse rather than
   * answer from an older slot.
   */
  async getMultipleAccounts(addresses: string[], opts: { commitment?: string; minContextSlot?: number } = {}): Promise<AccountsAt> {
    if (addresses.length > 100) throw new RelayerError('RpcError', `getMultipleAccounts takes at most 100 addresses, got ${addresses.length}`);
    const config: Record<string, unknown> = { encoding: 'base64', commitment: opts.commitment ?? 'confirmed' };
    if (opts.minContextSlot !== undefined) config.minContextSlot = opts.minContextSlot;
    const res = await this.call<{ context: { slot: number }; value: ({ data: [string, string]; owner: string; lamports: number } | null)[] }>('getMultipleAccounts', [addresses, config]);
    if (!res || !res.context || !Array.isArray(res.value)) throw new RelayerError('RpcError', 'getMultipleAccounts: response without context or value', res);
    if (res.value.length !== addresses.length) throw new RelayerError('RpcError', `getMultipleAccounts: ${res.value.length} accounts for ${addresses.length} addresses`, res);
    return { slot: res.context.slot, accounts: res.value.map((v) => (v ? { data: fromBase64(v.data[0]), owner: fromBase58(v.owner), lamports: BigInt(v.lamports) } : null)) };
  }

  /**
   * Every account of `program` matching all `filters`, with the context slot (`withContext`),
   * base64 data. `memcmp.bytes` is base58 unless `encoding` says otherwise.
   */
  async getProgramAccounts(program: string, filters: ProgramAccountsFilter[], opts: { commitment?: string; minContextSlot?: number } = {}): Promise<ProgramAccountsAt> {
    const config: Record<string, unknown> = { encoding: 'base64', commitment: opts.commitment ?? 'confirmed', withContext: true, filters };
    if (opts.minContextSlot !== undefined) config.minContextSlot = opts.minContextSlot;
    const res = await this.call<{ context: { slot: number }; value: { pubkey: string; account: { data: [string, string]; owner: string; lamports: number } }[] }>('getProgramAccounts', [program, config]);
    if (!res || !res.context || !Array.isArray(res.value)) throw new RelayerError('RpcError', 'getProgramAccounts: response without context (withContext ignored?)', res);
    return { slot: res.context.slot, accounts: res.value.map((v) => ({ pubkey: fromBase58(v.pubkey), account: { data: fromBase64(v.account.data[0]), owner: fromBase58(v.account.owner), lamports: BigInt(v.account.lamports) } })) };
  }

  async getBalance(address: string, commitment = 'confirmed'): Promise<bigint> {
    const res = await this.call<{ context: { slot: number }; value: number }>('getBalance', [address, { commitment }]);
    return BigInt(res.value);
  }

  /** The lamports a System-owned account of `dataLength` bytes needs to be exempt from rent collection (a System transfer that leaves a non-exempt, nonzero balance is rejected with `InsufficientFundsForRent`). */
  async getMinimumBalanceForRentExemption(dataLength: number): Promise<bigint> {
    return this.call<number>('getMinimumBalanceForRentExemption', [dataLength]).then(BigInt);
  }

  /** Returns the airdrop's transaction signature (base58); the caller confirms it. */
  async requestAirdrop(address: string, lamports: bigint): Promise<string> {
    return this.call<string>('requestAirdrop', [address, Number(lamports)]);
  }

  /** With `replaceRecentBlockhash`, the cluster simulates against its current slot (the signature check is then necessarily off): this is how a signed transaction whose blockhash has lapsed is shown to fail on the program's own `valid_until_slot` rule rather than on a stale blockhash. */
  async simulateTransaction(wire: Uint8Array, opts: { replaceRecentBlockhash?: boolean; minContextSlot?: number } = {}): Promise<SimulationResult> {
    const config: Record<string, unknown> = opts.replaceRecentBlockhash ? { encoding: 'base64', sigVerify: false, replaceRecentBlockhash: true, commitment: 'confirmed' } : { encoding: 'base64', sigVerify: true, commitment: 'confirmed' };
    if (opts.minContextSlot !== undefined) config.minContextSlot = opts.minContextSlot;
    // Like every other RPC method here, the payload is under `value` (`{ context, value: { err, logs, unitsConsumed, ... } }`);
    // reading `err`/`logs`/`unitsConsumed` off the top-level result (as this used to) silently produced `undefined` always,
    // so `sendV1`'s `if (sim.err) throw ...` preflight never actually caught a failing simulation.
    const res = await this.call<{ context: { slot: number }; value: { err: unknown; logs: string[] | null; unitsConsumed?: number } }>('simulateTransaction', [toBase64(wire), config]);
    return { err: res.value.err, logs: res.value.logs ?? [], unitsConsumed: res.value.unitsConsumed, slot: res.context.slot };
  }

  /** Returns the transaction signature (base58); we simulate ourselves first, so preflight is skipped here. `opts.beforeAttempt` as in `call`. */
  async sendTransaction(wire: Uint8Array, opts: { beforeAttempt?: () => void } = {}): Promise<string> {
    return this.call<string>('sendTransaction', [toBase64(wire), { encoding: 'base64', preflightCommitment: 'confirmed', skipPreflight: true }], opts);
  }

  /**
   * Polls `getSignatureStatuses` every 1.5 s until `confirmed`/`finalized`; throws on an on-chain
   * error or timeout. Returns the status entry's own `slot` field (R27): the slot the transaction
   * was processed in, i.e. its landing slot — not a later `getSlot` observation.
   */
  async confirm(signature: string, timeoutMs = 60_000): Promise<number> {
    const deadline = Date.now() + timeoutMs;
    for (;;) {
      const res = await this.call<{ value: ({ err: unknown; confirmationStatus?: string; slot?: number } | null)[] }>('getSignatureStatuses', [[signature], { searchTransactionHistory: true }]);
      const status = res.value[0];
      if (status) {
        if (status.err) throw new RelayerError('ConfirmFailed', `transaction ${signature} failed on chain: ${JSON.stringify(status.err)}`, status.err);
        if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') return status.slot ?? 0;
      }
      if (Date.now() >= deadline) throw new RelayerError('ConfirmTimeout', `transaction ${signature} did not confirm within ${timeoutMs} ms`);
      await this.sleepImpl(1_500);
    }
  }

  /**
   * Polls `getSignatureStatuses` every 1.5 s, as `confirm` does, but returns only when `isFinalizedSuccess` holds for
   * the entry (docs/18 5.1 D steps 4 and 6): a null entry and a `confirmed` one keep it polling. Throws `ConfirmFailed`
   * (with the error as `detail`) on any non-null `err`, `ConfirmTimeout` when `timeoutMs` passes first, and
   * `ConfirmAborted` as soon as `opts.abort()` returns true (checked before every read and after it), so a caller that
   * has locked makes no further read. Returns the entry and its own `slot`, the landing slot (R27).
   */
  async confirmFinalized(signature: string, timeoutMs = 90_000, opts: { abort?: () => boolean } = {}): Promise<{ slot: number; status: SignatureStatus }> {
    const deadline = Date.now() + timeoutMs;
    const aborted = () => new RelayerError('ConfirmAborted', `the wait for ${signature} to finalize was stopped`);
    for (;;) {
      if (opts.abort?.()) throw aborted();
      const res = await this.call<{ value: (SignatureStatus | null)[] }>('getSignatureStatuses', [[signature], { searchTransactionHistory: true }]);
      if (opts.abort?.()) throw aborted();
      const status = res.value[0] ?? null;
      if (status && status.err !== null && status.err !== undefined) throw new RelayerError('ConfirmFailed', `transaction ${signature} failed on chain: ${JSON.stringify(status.err)}`, status.err);
      if (status && isFinalizedSuccess(status)) return { slot: status.slot ?? 0, status };
      if (Date.now() >= deadline) throw new RelayerError('ConfirmTimeout', `transaction ${signature} was not finalized within ${timeoutMs} ms`);
      await this.sleepImpl(1_500);
    }
  }

  async getTransaction(signature: string): Promise<unknown> {
    return this.call('getTransaction', [signature, { maxSupportedTransactionVersion: 1, encoding: 'json', commitment: 'confirmed' }]);
  }

  /**
   * Like `confirm`, but never throws on an on-chain error: polls
   * `getSignatureStatuses` until `confirmed`/`finalized` and returns the
   * status's own `slot` (R27) together with whatever `err` the cluster
   * reports (`null` on success). Used by `sendV1Unchecked`, where a landed
   * rejection is the expected, interesting outcome, not a failure to throw.
   */
  async confirmStatus(signature: string, timeoutMs = 60_000): Promise<{ slot: number; err: unknown }> {
    const deadline = Date.now() + timeoutMs;
    for (;;) {
      const res = await this.call<{ value: ({ err: unknown; confirmationStatus?: string; slot?: number } | null)[] }>('getSignatureStatuses', [[signature], { searchTransactionHistory: true }]);
      const status = res.value[0];
      if (status && (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized')) {
        return { slot: status.slot ?? 0, err: status.err };
      }
      if (Date.now() >= deadline) throw new RelayerError('ConfirmTimeout', `transaction ${signature} did not confirm within ${timeoutMs} ms`);
      await this.sleepImpl(1_500);
    }
  }
}

export interface SendResult {
  signature: string;
  wire: Uint8Array;
  /** The transaction's landing slot, from `getSignatureStatuses`' own `slot` field (R27). This is the transaction slot, not a later observation. */
  transactionSlot: number;
  /** A fresh `getSlot` call made after confirmation, i.e. the caller's current-chain-slot observation at that later moment — not the transaction's landing slot (R27). */
  observedSlot: number;
  unitsConsumed: number | undefined;
}

/** Sign with `signV1`, then simulate, send and confirm through `sendSignedV1`, so there is one send path. */
export async function sendV1(rpc: Rpc, message: V1Message, signer: RelayerSigner): Promise<SendResult> {
  return sendSignedV1(rpc, await signV1(message, signer));
}

/**
 * The points of `sendSignedV1` where `opts.checkpoint` runs, both before the send: `simulated`, directly after the
 * simulation's await, and `attempt`, synchronously before every `sendTransaction` request, the first and each retry.
 */
export type SendStage = 'simulated' | 'attempt';

/**
 * Simulate a finished wire first (throwing `SimulationFailed` with the raw error, logs and units on failure), then
 * send and confirm it. For a wire signed elsewhere: the sweep's, which the worker builds and signs and the main thread
 * sends (docs/18-one-fee-reserve.md 2.4 and section 6). `sendV1` ends here after signing, with no checkpoint.
 *
 * Section 6 ("Lock and Forget during the sweep's awaits") promises no send after Lock. `opts.checkpoint` is where the
 * engine's `assertOpen` will run: after the simulation's await (`simulated`) and before every send attempt
 * (`attempt`), retries on HTTP 429 and network errors included, since `Rpc.call` retries inside one `sendTransaction`.
 * A throw there propagates as it is, and no request is made after it. There is no checkpoint after the send: section 6
 * lets a lock after the send finish the confirmation and show the outcome, and nothing further is sent either way.
 */
export async function sendSignedV1(rpc: Rpc, wire: Uint8Array, opts: { checkpoint?: (stage: SendStage) => void } = {}): Promise<SendResult> {
  const sim = await rpc.simulateTransaction(wire);
  opts.checkpoint?.('simulated');
  if (sim.err) throw new RelayerError('SimulationFailed', `simulation failed: ${JSON.stringify(sim.err)}`, { err: sim.err, logs: sim.logs, unitsConsumed: sim.unitsConsumed });
  const signature = await rpc.sendTransaction(wire, { beforeAttempt: () => opts.checkpoint?.('attempt') });
  const transactionSlot = await rpc.confirm(signature);
  const observedSlot = await rpc.getSlot();
  return { signature, wire, transactionSlot, observedSlot, unitsConsumed: sim.unitsConsumed };
}

/**
 * Sign, simulate (recorded but never thrown on), send with preflight
 * skipped, and confirm without throwing on an on-chain error: the mainnet
 * canary's step 5 needs a `min_out` rejection to land on chain, not be
 * intercepted locally. The simulation is archived as the "mainnet dry-run"
 * even though its own failure does not stop the send.
 */
export async function sendV1Unchecked(rpc: Rpc, message: V1Message, signer: RelayerSigner): Promise<SendResult & { err: unknown; simulation: SimulationResult }> {
  const wire = await signV1(message, signer);
  const simulation = await rpc.simulateTransaction(wire);
  const signature = await rpc.sendTransaction(wire);
  const { slot: transactionSlot, err } = await rpc.confirmStatus(signature);
  const observedSlot = await rpc.getSlot();
  return { signature, wire, transactionSlot, observedSlot, unitsConsumed: simulation.unitsConsumed, err, simulation };
}
